// Origin-Destination jobs: build the request body, run it through dataJobs.ts (submit, poll, read)
// and normalise the rows. All network calls go through http.ts; nothing is logged.
import type { CountryCode } from '../config/countries';
import { LONG_JOB_TIMING, mapOutcomeRows, runDataJob } from './dataJobs';
import type { JobOutcome, RunJobOptions } from './dataJobs';
import { nullableNumber, text, unwrap } from './rowValues';
import { isDemoRouted, runDemoOdmJob } from './demoApi';
import { draftOdmJob, isDraftMocked } from './draftMocks';
import { cellsAtRung, isModelGrid, largestRung, rowAreaKey } from './catalogueRequests';

export interface ODMJobParams {
  agg_type: 'odm';
  measure: 'trips';
  start_time: string;
  end_time: string;
  activity_type: 'daily' | 'hourly';
  origin_geo_type: string;
  origin_geo_ids: Array<number | string>;
  destination_geo_type: string;
  destination_geo_ids: Array<number | string>;
  country_code: CountryCode;
  /** True asks for every row as a download link instead of a 100-row preview. */
  full: boolean;
  /** Model-grid areas only: the one cell size both ends are read at (grid_1000m and the like). */
  rung?: string;
}

/**
 * One origin-destination row as the backend delivers it. Only what the data holds is typed:
 * the trip count, the optional distance and speed, and the ids. There are no per-mode
 * (walk, bike, car, transit) columns.
 */
export interface ODMResult {
  batch_date: string;
  /**
   * When the row's trips happened: an hour on hourly rows, and the delivery date on daily rows
   * (the same value as batch_date). The backend can also send null, so a reader falls back to
   * batch_date.
   */
  time_bucket: string | null;
  /** Kept as strings for display; ids are sent from the selection, not from results. */
  origin_geo_id: string;
  destination_geo_id: string;
  trips: number;
  General_Distance: number | null;
  Average_Speed: number | null;
}

/**
 * Normalise one row of an ODM preview. Missing numbers stay null instead of becoming 0, so a
 * missing distance or speed is shown as missing; `trips` is the one count and falls back to 0.
 */
export const normalizeODMResult = (r: any): ODMResult => ({
  batch_date: text(r.batch_date),
  time_bucket: unwrap(r.time_bucket) == null ? null : text(r.time_bucket),
  // Grid rows are keyed with their size, as a grid area saves its cells.
  origin_geo_id: rowAreaKey(text(r.origin_geo_id), unwrap(r.origin_geo_type)),
  destination_geo_id: rowAreaKey(text(r.destination_geo_id), unwrap(r.destination_geo_type)),
  trips: nullableNumber(r.trips) ?? 0,
  General_Distance: nullableNumber(r.General_Distance),
  Average_Speed: nullableNumber(r.Average_Speed),
});

/** Submit an Origin-Destination job and wait for its rows. */
export async function runOdmJob(params: ODMJobParams, o: RunJobOptions = {}): Promise<JobOutcome<ODMResult>> {
  if (!params.full && isDraftMocked(params.country_code, 'odm', params.origin_geo_type)) return draftOdmJob(params);
  if (isDemoRouted(params.country_code) && !params.full) return runDemoOdmJob(params);
  const outcome = await runDataJob<unknown>(odmRequestBody(params), { timing: LONG_JOB_TIMING, ...o });
  return mapOutcomeRows(outcome, normalizeODMResult);
}

/**
 * The /data-agg body of an Origin-Destination request. A model-grid area is read at one cell size (`rung`, by
 * default its largest): both ends go as that size's square ids. An empty end stays empty (anywhere).
 */
export function odmRequestBody(params: ODMJobParams): Omit<ODMJobParams, 'rung'> {
  const { rung: asked, ...body } = params;
  if (!isModelGrid(params.origin_geo_type) && !isModelGrid(params.destination_geo_type)) return body;
  const rung = asked ?? largestRung([...params.origin_geo_ids, ...params.destination_geo_ids]);
  if (!rung) throw new Error('This grid area holds no cells the API can read.');
  return {
    ...body,
    origin_geo_type: rung,
    origin_geo_ids: cellsAtRung(params.origin_geo_ids, rung).ids,
    destination_geo_type: rung,
    destination_geo_ids: cellsAtRung(params.destination_geo_ids, rung).ids,
  };
}
